import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Drawer } from '../../components/Drawer';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import {
  useResellerAddOns, useResellerPackages, addResellerAddOn, updateResellerAddOn,
  type ResellerAddOn,
} from '../../api/reseller';

/**
 * Reseller "My add-ons" — the reseller's OWN à-la-carte extras, mirroring the
 * platform admin's Add-ons editor but scoped to this reseller: rows carry the
 * reseller id, compatible packages are the reseller's own catalog, and the
 * add-on is visible/sellable ONLY to this reseller's customers. Money flows
 * through the normal invoice+payment path, so the platform commission applies
 * exactly like package payments.
 */

/** The entitlement-boost delta fields, in display order — mirrors add_ons.conditions. */
const BOOST_FIELDS = [
  { key: 'maxProducts', label: 'Extra products' },
  { key: 'maxInvoices', label: 'Extra invoices / month' },
  { key: 'maxUsers', label: 'Extra users' },
  { key: 'maxLocations', label: 'Extra locations' },
] as const;

export function ResellerAddons() {
  const { data: addons = [], isLoading } = useResellerAddOns();
  const { data: packages = [] } = useResellerPackages();
  const queryClient = useQueryClient();
  const [drawer, setDrawer] = useState<{ kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; addon: ResellerAddOn }>({ kind: 'closed' });

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Pricing"
        title="My add-ons"
        subtitle="À-la-carte extras your customers can buy on top of your packages — you set the name and price."
        action={<Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => setDrawer({ kind: 'create' })}>New add-on</Button>}
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-700">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3 min-w-[280px]">Add-on</th>
                <th className="text-right font-medium px-4 py-3">Price</th>
                <th className="text-left font-medium px-4 py-3">Billing</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell min-w-[220px]">Compatible packages</th>
                <th className="text-right font-medium px-4 py-3 hidden md:table-cell">In use</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3">
                      <Skeleton className="h-4 w-40 max-w-full rounded mb-1.5" />
                      <Skeleton className="h-3 w-56 max-w-full rounded" />
                    </td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded" /></td>
                    <td className="px-4 py-3 hidden lg:table-cell"><Skeleton className="h-4 w-32 rounded" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-8 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-5 sm:px-6 py-3"></td>
                  </tr>
                ))
              ) : addons.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No add-ons yet. Create one — e.g. “Extra location, ৳500/year” — and your
                    customers can buy it from their portal on top of their package.
                  </td>
                </tr>
              ) : (
                addons.map((a) => {
                  const compatibleNames = packages
                    .filter((p) => a.compatiblePackageIds.includes(p.id))
                    .map((p) => p.name);
                  return (
                    <tr key={a.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                      <td className="px-5 sm:px-6 py-3">
                        <div className="font-medium text-zinc-950 dark:text-zinc-50">{a.name}</div>
                        <div className="mt-1 text-xs text-zinc-600 dark:text-zinc-300 max-w-md leading-snug">{a.description}</div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Money minor={a.amount} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium" />
                        <span className="text-xs text-zinc-500 dark:text-zinc-400"> {a.unitLabel}</span>
                      </td>
                      <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 whitespace-nowrap">
                        {a.billing === 'recurring' ? 'Recurring' : 'One-time'}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {compatibleNames.length === 0 ? (
                          <span className="text-xs text-zinc-500 dark:text-zinc-400">All your packages</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {compatibleNames.map((name) => (
                              <span key={name} className="px-1.5 py-px rounded text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200">
                                {name}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200 hidden md:table-cell">{a.inUseCount}</td>
                      <td className="px-4 py-3">
                        <Badge tone={a.status === 'active' ? 'success' : 'neutral'}>
                          {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="px-5 sm:px-6 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setDrawer({ kind: 'edit', addon: a })}
                          className="px-2.5 py-1 rounded text-xs font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
        Add-ons are charged on top of the base subscription through the normal invoice flow —
        the platform commission applies to add-on payments exactly like package payments.
      </p>

      {drawer.kind !== 'closed' && (
        <ResellerAddonDrawer
          mode={drawer}
          onClose={() => setDrawer({ kind: 'closed' })}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['reseller', 'addons'] });
            setDrawer({ kind: 'closed' });
          }}
        />
      )}
    </div>
  );
}

function ResellerAddonDrawer({
  mode,
  onClose,
  onSaved,
}: {
  mode: { kind: 'create' } | { kind: 'edit'; addon: ResellerAddOn };
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = mode.kind === 'edit';
  const seed = editing ? mode.addon : null;
  const { data: packages = [] } = useResellerPackages();

  const [name, setName] = useState(seed?.name ?? '');
  const [description, setDescription] = useState(seed?.description ?? '');
  const [billing, setBilling] = useState<'recurring' | 'one_time'>(seed?.billing ?? 'recurring');
  // Stored MINOR units; the input is BDT → seed ÷100, save ×100.
  const [price, setPrice] = useState(seed ? String(seed.amount / 100) : '');
  const [status, setStatus] = useState<'active' | 'draft' | 'archived'>(
    seed?.status === 'draft' ? 'draft' : seed?.status === 'archived' ? 'archived' : 'active',
  );
  const [pkgSelection, setPkgSelection] = useState<Set<string>>(() => new Set(seed?.compatiblePackageIds ?? []));
  const [boost, setBoost] = useState<Record<string, string>>(() => {
    const c = seed?.conditions ?? undefined;
    const out: Record<string, string> = {};
    for (const f of BOOST_FIELDS) {
      const v = c ? (c as Record<string, unknown>)[f.key] : undefined;
      out[f.key] = typeof v === 'number' ? String(v) : '';
    }
    return out;
  });
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function togglePkg(id: string) {
    const next = new Set(pkgSelection);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPkgSelection(next);
  }

  const priceNum = Number(price);
  const valid = name.trim().length >= 2 && Number.isFinite(priceNum) && priceNum >= 0;

  async function handleSave() {
    if (!valid || submitting) return;
    setSubmitting(true);
    setSaveError(null);
    const conditions: Record<string, number> = {};
    for (const f of BOOST_FIELDS) {
      const raw = boost[f.key]?.trim();
      const v = raw ? Number(raw) : NaN;
      if (raw && Number.isFinite(v) && v > 0) conditions[f.key] = Math.round(v);
    }
    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      billing,
      priceMinor: Math.round(priceNum * 100),
      status,
      packageIds: [...pkgSelection],
      conditions: Object.keys(conditions).length ? conditions : null,
    };
    try {
      if (editing) await updateResellerAddOn(seed!.id, payload);
      else await addResellerAddOn(payload);
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save the add-on. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={editing ? `Edit add-on · ${seed!.name}` : 'New add-on'}
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSave} loading={submitting} disabled={!valid}>
            {editing ? 'Save' : 'Create add-on'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Input label="Name" required placeholder="Extra location" value={name} onChange={(e) => setName(e.target.value)} />
        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Description</label>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Each location adds 1 to your max."
            className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 placeholder-zinc-400 dark:placeholder-zinc-500 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand resize-none"
          />
        </div>
        <Input
          label="Price (BDT)"
          required
          prefix={<span className="text-sm">৳</span>}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          hint={billing === 'recurring' ? 'Charged per billing period on top of the subscription.' : 'Charged once at purchase.'}
        />

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Billing</span>
          <div className="space-y-2">
            <Radio name="billing" checked={billing === 'recurring'} onChange={() => setBilling('recurring')} label="Recurring with subscription" />
            <Radio name="billing" checked={billing === 'one_time'} onChange={() => setBilling('one_time')} label="One-time charge at purchase" />
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Compatible packages
            <span className="ml-1.5 text-xs font-normal text-zinc-500 dark:text-zinc-400">
              ({pkgSelection.size} of {packages.length} selected — none selected = available on all your packages)
            </span>
          </span>
          <div className="grid grid-cols-2 gap-2">
            {packages.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pkgSelection.has(p.id)}
                  onChange={() => togglePkg(p.id)}
                  className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                />
                {p.name}
              </label>
            ))}
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Entitlement boost
            <span className="ml-1.5 text-xs font-normal text-zinc-500 dark:text-zinc-400">
              (optional — adds on top of the plan's cap while attached; leave blank for none)
            </span>
          </span>
          <div className="grid grid-cols-2 gap-3">
            {BOOST_FIELDS.map((f) => (
              <Input
                key={f.key}
                label={f.label}
                type="number"
                min={1}
                placeholder="—"
                value={boost[f.key] ?? ''}
                onChange={(e) => setBoost((prev) => ({ ...prev, [f.key]: e.target.value }))}
              />
            ))}
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Status</span>
          <div className="space-y-2">
            <Radio name="status" checked={status === 'active'} onChange={() => setStatus('active')} label="Active — customers can buy it" />
            <Radio name="status" checked={status === 'draft'} onChange={() => setStatus('draft')} label="Draft — hidden from customers" />
            {editing && (
              <Radio name="status" checked={status === 'archived'} onChange={() => setStatus('archived')} label="Archived — retired (existing purchases keep working)" />
            )}
          </div>
        </div>

        {saveError && (
          <Banner tone="error" title="Couldn’t save the add-on">
            {saveError}
          </Banner>
        )}
      </div>
    </Drawer>
  );
}

function Radio({
  name,
  checked,
  onChange,
  label,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="text-brand focus:ring-brand" />
      {label}
    </label>
  );
}
